import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "@emotion/react";
import SendSharpIcon from "@mui/icons-material/SendSharp";
import MinimizeSharpIcon from "@mui/icons-material/MinimizeSharp";
//
import { Box, CircularProgress, IconButton, Typography } from "@/components";
import { useResizeObserver, useSettings } from "@/hooks";
import { base64ToText } from "@/utils/base64";
//
import {
  StyledAvatar,
  StyledCard,
  StyledCardActions,
  StyledCardContent,
  StyledCardHeader,
  StyledFrame,
  StyledInput,
} from "./style";
import MessageList from "./components/MessageList";
import { conversationService } from "@/services";

const defaultOptions = {};

const Chat = () => {
  const [{ assistantName }] = useSettings(["APP_ASSISTANT_NAME:assistantName"]);
  const currentRef = useResizeObserver({});
  const navigate = useNavigate();
  const location = useLocation();
  const { channelId } = useParams();
  const inputRef = useRef(null);
  const [title, setTitle] = useState(null);
  const [subtitle, setSubtitle] = useState("AGENTE VIRTUAL");
  const [isSending, setIsSending] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageBuffer, setMessageBuffer] = useState({});
  const [realMessage, setRealMessage] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [transhipment, setTranshipment] = useState(null);
  const theme = useTheme();

  const settings = useMemo(() => {
    const fragment = location.hash.replace("#", "");
    const options = fragment ? JSON.parse(base64ToText(fragment)) : {};

    return {
      ...defaultOptions,
      ...options,
    };
  }, [location]);

  const inputDisabled = useMemo(() => {
    return isSending || isWaiting;
  }, [isSending, isWaiting]);

  const [expand, setExpand] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const handleValid = () => {
    setIsValid(inputRef.current.value.trim() !== "");
  };

  const handleMinimize = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Colapsa a UI interna (animação de saída)
    setExpand(false);

    // Sinaliza ao embed para reduzir o iframe (sem navegar)
    try {
      if (window.parent) {
        // Envia comando explícito de minimizar, com dimensões fixas
        window.parent.postMessage(
          { type: "_CHAT_MINIMIZE", width: 350, height: 120 },
          "*",
        );
      }
    } catch (_) {}

    // Volta para a raiz mantendo o hash
    setTimeout(() => {
      navigate({ pathname: "/", hash: location.hash || "" });
    }, theme.transitions.duration.shorter);
  };

  const handleIntent = (args) => {
    const { uid, name, parameters = null } = args;

    if (parameters !== null) {
      const { title, frame: frameProps = null } = parameters;

      if (frameProps !== null) {
        setTitle(title);
        setSubtitle(null);
        setTranshipment(frameProps);
      }
    }
  };

  const handleFeedback = async (args) => {
    const { messageId, ...otherArgs } = args;

    const { result } = await conversationService.feedback(
      conversationId,
      messageId,
      otherArgs,
    );

    setMessages((prevMessages) =>
      [...prevMessages].map((item) => {
        if (item.id === messageId && args.type) {
          item.feedback = args.type;
        }

        return item;
      }),
    );

    // Não empurrar feedback negativo para o fluxo de mensagens.
    // Apenas garantir que não há metadados de feedback ativos.
    setMessageBuffer({});
  };

  const handleDataEvent = async (data) => {
    const result = await data.json();
    if (!result) {
      return;
    }
    const buffer = {
      id: result.message.id,
      asking: false,
      content: null,
      status: result.status,
      options: result.options,
      action: result.action,
    };

    if (
      "feedback_mode_active" in result.message.metadata &&
      !result.message.metadata.feedback_mode_active
    ) {
      setMessageBuffer({});
    }

    if (result.message.content.content_type === "TEXT") {
      buffer.content = result.message.content.parts;
    }

    if (result.status === "FINISHED") {
      setMessages((prevMessages) => [...prevMessages, buffer]);
      setRealMessage("");
    } else {
      setRealMessage((prevMessage) => ({ ...prevMessage, ...buffer }));
    }
  };

  const handleMessage = async (metadata = {}) => {
    if (!inputRef.current) {
      return;
    }

    setIsWaiting(true);
    setIsSending(true);

    try {
      const question = inputRef.current.value;
      const messageLimit = 10;
      const relevantMessages =
        messages.length > messageLimit
          ? messages.slice(messageLimit / -1)
          : messages.slice(1);
      const placeholder = relevantMessages.map((value) => {
        const key = value.asking ? "human" : "ai";

        return {
          [key]: value.content,
        };
      });
      const message = {
        id: uuidv4(),
        content: {
          content_type: "TEXT",
          parts: [question],
        },
        metadata: { ...messageBuffer, ...metadata },
      };

      if (question) {
        setMessages([
          ...messages,
          {
            id: message.id,
            asking: true,
            content: question,
          },
        ]);
      }

      const response = conversationService.message(message, conversationId, {
        placeholder,
      });

      response.on("init", (value) => {
        setConversationId(value.replace(/#([^ ]+).*$/, "$1"));
        setIsSending(false);
        setRealMessage({
          id: null,
          asking: false,
          content: null,
          status: "THINKING",
        });
      });
      response.on("data", handleDataEvent);
      response.on("done", () => {
        setIsSending(false);
        setIsWaiting(false);
      });

      inputRef.current.value = null;
    } catch (e) {
      setIsSending(false);
      setIsWaiting(false);
    }
  };

  const handleKeyDown = (callback) => (event) => {
    if (event.key === "Enter") {
      callback();
    }
  };

  useEffect(() => {
    setExpand(true);

    const id = setTimeout(() => handleMessage(), 300);

    return () => {
      clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (channelId) {
      conversationService.setChannelId(channelId);
      try {
        sessionStorage.setItem("chat:channelId", channelId);
      } catch (_) {}
    }
  }, [channelId]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputDisabled]);

  return (
    <StyledCard ref={currentRef} expand={expand.toString()}>
      <StyledCardHeader
        id="chat-head"
        avatar={
          <StyledAvatar
            aria-label="recipe"
            alt="IARA"
            src="/static/images/avatar/1_cut.png"
          />
        }
        action={
          <IconButton
            aria-label="settings"
            color="gray3"
            onClick={handleMinimize}
          >
            <MinimizeSharpIcon />
          </IconButton>
        }
        title={
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="common.white"
            noWrap
          >
            {title ?? assistantName}
          </Typography>
        }
        subheader={
          <Typography
            variant="subtitle2"
            color="common.white"
            marginTop={-0.5}
            noWrap
          >
            {subtitle}
          </Typography>
        }
      />
      {transhipment ? (
        <StyledFrame {...transhipment} />
      ) : (
        <>
          <StyledCardContent>
            <MessageList
              id="chat-history"
              data={messages}
              runtime={realMessage}
              onIntent={handleIntent}
              onFeedback={handleFeedback}
            />
          </StyledCardContent>
          <StyledCardActions id="chat-text-box" disableSpacing>
            <StyledInput
              ref={inputRef}
              placeholder="Digite sua mensagem..."
              disabled={inputDisabled}
              onKeyDown={handleKeyDown(handleMessage)}
              onChange={handleValid}
          onFocus={() => {
            // Fecha o popover de feedback quando o usuário foca na barra de texto
            window.dispatchEvent(new CustomEvent("close-feedback-popover"));
          }}
            />
            <Box marginTop={-2} marginBottom={-2}>
              {isSending ? (
                <CircularProgress size="22px" />
              ) : (
                <IconButton disabled={!isValid} onClick={() => handleMessage()}>
                  <SendSharpIcon />
                </IconButton>
              )}
            </Box>
          </StyledCardActions>
        </>
      )}
    </StyledCard>
  );
};

export default Chat;
